<template>
  <div class="chart-savings">
    <VueDatePicker
      class="chart-savings-datepicker"
      dark
      month-picker
      v-model="date"
      locale="fr"
      auto-apply
      :format="format"
      @update:model-value="fetchMonth"
      :clearable="false"
    />
    <CommonInlineData
      style="min-width: 300px"
      :label="`Économie du mois ${getMonth()}`"
      :data="`${sum()} €`"
    />
    <apexchart
      v-if="!fetching"
      class="chart-savings-chart"
      :series="dataset.filter((e) => e.value ?? 0 > 0).map((e) => e.value)"
      :options="{
        chart: {
          type: 'donut'
        },
        legend: {
          show: false
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          y: {
            formatter: (v: number) => {
              return `${v} €`
            }
          }
        },
        colors: ['#0055A3', '#0072CE', '#4D9FE8'],
        labels: dataset.map((e) => get_device_name(e.label)),
        plotOptions: {
          bar: {
            borderRadius: 0,
            borderWidth: 0
          },
          pie: {
            donut: {
              size: '30%'
            }
          }
        }
      }"
    />
    <CommonLoader class="chart-savings-loader" v-else />
  </div>
</template>

<script setup lang="ts">
import CommonInlineData from '@/components/common/CommonInlineData.vue'
import type { ApplicationTreeNode } from '@/service/service_unit'

import { onMounted, ref } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import CommonLoader from '@/components/common/CommonLoader.vue'
import { useOrganizationUnitStore } from '@/stores/store_organization_unit'
import { get_device_name } from '@/service/service_device'

const dataset = ref<{ label: string; value?: number }[]>([])
const current_day = new Date()
const unit_store = useOrganizationUnitStore()
const fetching = ref(true)
const date = ref({ month: current_day.getMonth(), year: current_day.getFullYear() })
const props = defineProps<{
  node: ApplicationTreeNode
}>()

const format = (date: Date) => {
  return date.toLocaleDateString('fr', { day: undefined, month: 'long', year: 'numeric' })
}

function sum() {
  let output = 0
  dataset.value.map((e) => {
    output += e.value ?? 0
  })

  return Number(Math.round(output).toString())
}

function getMonth() {
  let appostroph = false
  if ([3, 7, 9].includes(date.value.month)) {
    appostroph = true
  }

  const parsed_date = new Date(date.value.year, date.value.month)
  const month_name = parsed_date.toLocaleDateString('fr', {
    day: undefined,
    month: 'long',
    year: undefined
  })
  let result = month_name.charAt(0).toUpperCase() + month_name.slice(1)
  return `${appostroph ? "d'" : 'de '}${result}`
}

async function fetchMonth() {
  fetching.value = true
  unit_store
    .fetchDashboard(props.node, new Date(date.value.year, date.value.month, 1))
    .then((response) => {
      if (response.data) {
        dataset.value = Object.keys(response.data.eur).map((k) => ({
          label: k,
          value: response.data.eur[k]
        }))
      }
    })
    .finally(() => {
      fetching.value = false
    })
}

onMounted(fetchMonth)
</script>

<style scoped>
.chart-savings {
  display: flex;
  position: relative;
  flex-direction: column;
}

.chart-savings-loader {
  position: absolute;
  padding: 0;
  top: 30px;
  right: -80px;
}

.chart-savings-chart {
  position: absolute;
  width: 150px;
  height: 150px;
  right: -130px;
  top: 0;
}
</style>
